import React, { useState } from 'react'
import { Card } from "@/components/ui/Card";
import RHFInput from "@/components/form/RHFInput";
import { FormProvider, useForm } from "react-hook-form";
import { passwordSchema, passwordDefaultValue } from '../validation.schema';
import AuthService from '@/services/authService';
import { zodResolver } from '@hookform/resolvers/zod';
import { ButtonShadcn as Button } from "@/components/ui/Button";
import toast from 'react-hot-toast';
import { Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Modal';

// Shared styling for all input fields
// Added right padding so the eye icon doesn't overlap typed text
const INPUT_STYLE = "w-full rounded-md px-4 py-2.5 pr-11 text-sm bg-background-secondary text-text-primary focus:outline-none border";

const PasswordChange = () => {

    // Loading state while password is being updated
    const [changingPassword, setChangingPassword] = useState(false);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingPasswordData, setPendingPasswordData] = useState(null);

    // Independent visibility toggle per field
    const [visibility, setVisibility] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });

    const toggleVisibility = (field) => {
        setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    // React Hook Form setup with Zod validation
    const passwordMethods = useForm({
        resolver: zodResolver(passwordSchema),
        mode: "onSubmit",
        defaultValues: passwordDefaultValue,
    });

    // Extract form helper methods
    const {
        handleSubmit: handlePasswordSubmit,
        reset: resetPwdForm
    } = passwordMethods;

    // Handle password change submission
    const onChangePassword = async (data) => {
        try {
            // Start loading state
            setChangingPassword(true);

            // API call to update password
            await AuthService.changePassword(
                data.currentPassword,
                data.newPassword,
                data.confirmPassword
            );

            // Show success notification
            toast.success("Password changed successfully");

            // Reset form fields after success
            resetPwdForm();

        } catch (err) {
            console.error(err);

            // Show error notification
            toast.error("Failed to change password");

        } finally {
            // Stop loading state
            setChangingPassword(false);
        }
    };

     const handleValidated = (data) => {
        setPendingPasswordData(data);
        setShowConfirmModal(true);
    };

    const handleConfirmChange = async () => {
        if (!pendingPasswordData) return;
        const data = pendingPasswordData;
        setShowConfirmModal(false);
        setPendingPasswordData(null);
        await onChangePassword(data);
    };

    const handleCancelChange = () => {
        setShowConfirmModal(false);
        setPendingPasswordData(null);
    };

    // Reusable field + toggle wrapper
    const renderPasswordField = ({ name, label, placeholder }) => (
        <div className="relative">
            <RHFInput
                name={name}
                label={label}
                placeholder={placeholder}
                type={visibility[name] ? "text" : "password"}
                inputClass={INPUT_STYLE}
                required
            />

            <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => toggleVisibility(name)}
                tabIndex={-1}
                className="absolute right-3 top-1/2 h-auto w-auto translate-y-[6px] text-text-secondary hover:text-text-primary hover:bg-transparent transition-colors"
                aria-label={visibility[name] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
            >
                {visibility[name] ? <EyeOff size={16} /> : <Eye size={16} />}
            </Button>
        </div>
    );

    return (
        <Card className="p-5">

            {/* Section header */}
            <div className="mb-4">
                <h2 className="text-sm font-semibold text-text-primary">
                    Security
                </h2>

                <p className="text-xs text-text-secondary mt-1">
                    Change your password.
                </p>
            </div>

            {/* Form provider for React Hook Form */}
            <FormProvider {...passwordMethods}>

                {/* Password update form */}
                <form
                    onSubmit={handlePasswordSubmit(handleValidated)}
                    className="space-y-4"
                >

                    {/* Current password input */}
                    {renderPasswordField({
                        name: "currentPassword",
                        label: "Current password",
                        placeholder: "Enter current password",
                    })}

                    {/* New password input */}
                    {renderPasswordField({
                        name: "newPassword",
                        label: "New password",
                        placeholder: "Enter new password",
                    })}

                    {/* Confirm password input */}
                    {renderPasswordField({
                        name: "confirmPassword",
                        label: "Confirm new password",
                        placeholder: "Confirm new password",
                    })}

                    {/* Submit button */}
                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={changingPassword}
                            className="rounded-xl px-5 py-2.5 bg-button-secondary text-text-base hover:bg-button-secondary/80"
                        >
                            {changingPassword ? "Updating..." : "Update password"}
                        </Button>
                    </div>

                </form>
            </FormProvider>
            <Dialog open={showConfirmModal} onOpenChange={(open) => !open && handleCancelChange()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm password change</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to change your password? You'll need to sign in with your new password next time.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={handleCancelChange}
                            disabled={changingPassword}
                            className="rounded-xl px-5 py-2.5 border border-border-primary bg-background-tertiary text-text-primary hover:bg-background-tertiary/90"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleConfirmChange}
                            disabled={changingPassword}
                            className="rounded-xl px-5 py-2.5 bg-button-secondary text-text-base hover:bg-button-secondary/80"
                        >
                            {changingPassword ? "Updating..." : "Yes, change password"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    )
}

export default PasswordChange